// slices/exams/examsSlice.ts
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { UserExam } from '../../types/User';
import { logout } from '../auth/authSlice';
import {
  isStepExam,
  isShelfExam,
  isSharedCustomExam,
  isPersonalExam,
  getExamAccessLevel,
  isComlexExam,
  isPreclinicalExam,
  isResidencyExam,
  isNclexExam,
} from '../../utils/examUtils';
import { SchoolState } from '../school/schoolSlice';
import { SubscriptionAccess } from '../../types/Subscription';

type ExamsStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// Use createEntityAdapter for normalization boilerplate
const examsAdapter = createEntityAdapter<UserExam>();

type ExamStatsUpdate = Pick<UserExam, 'id'> &
  Partial<
    Pick<UserExam, 'lectureCount' | 'videos' | 'flashcards' | 'vignettes'>
  >;

// Define the combined root state type for selectors that need access to multiple slices
interface RootState {
  exams: ExamsState;
  school: SchoolState;
}

interface ExamsState extends ReturnType<typeof examsAdapter.getInitialState> {
  primaryExamId: string | null;
  status: ExamsStatus;
  error: string | null;
  lastFetchedAt: number | null;
}

const initialState: ExamsState = examsAdapter.getInitialState({
  primaryExamId: null,
  status: 'idle',
  error: null,
  lastFetchedAt: null,
});

const examsSlice = createSlice({
  name: 'exams',
  initialState,
  reducers: {
    examsLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    // Action to set all exams (expects normalized data or raw array)
    examsReceived: (state, action: PayloadAction<UserExam[]>) => {
      const exams = action.payload;
      const primaryExam = exams.find((exam) => exam.primary);
      examsAdapter.setAll(state, exams); // Use adapter to set entities and ids
      state.primaryExamId = primaryExam ? primaryExam.id : null;
      state.status = 'succeeded';
      state.error = null;
      state.lastFetchedAt = Date.now();
    },
    examCreated: (state, action: PayloadAction<UserExam>) => {
      const newExam = action.payload;
      if (newExam.isActiveCustomExam === true) {
        Object.values(state.entities).forEach((exam) => {
          if (exam && exam.id !== newExam.id && exam.type === 'CUSTOM') {
            exam.isActiveCustomExam = false;
          }
        });
      }
      // If the new exam is primary, ensure all others are not
      if (newExam.primary) {
        Object.values(state.entities).forEach((exam) => {
          if (exam) {
            exam.primary = false;
          }
        });

        state.primaryExamId = newExam.id;
      }
      // Add the new exam
      examsAdapter.addOne(state, newExam);
    },
    examUpdated: (
      state,
      action: PayloadAction<Partial<UserExam> & { id: string }>
    ) => {
      // Use adapter to update a single exam
      examsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      });

      if (action.payload.isActiveCustomExam === true) {
        Object.values(state.entities).forEach((exam) => {
          if (exam && exam.id !== action.payload.id && exam.type === 'CUSTOM') {
            exam.isActiveCustomExam = false;
          }
        });
      }

      // If the 'primary' status was explicitly set to true in the update
      if (action.payload.primary === true) {
        // Ensure this exam is the only primary one
        Object.values(state.entities).forEach((exam) => {
          if (exam && exam.id !== action.payload.id) {
            exam.primary = false;
          }
        });

        // Update the primaryExamId
        state.primaryExamId = action.payload.id;
      }
      // If the 'primary' status was explicitly set to false and this was the primary exam
      else if (
        action.payload.primary === false &&
        state.primaryExamId === action.payload.id
      ) {
        // Remove the primaryExamId as this exam is no longer primary
        state.primaryExamId = null;
        // Note: We are not automatically assigning a new primary here.
        // If needed, additional logic could find another exam to set as primary.
      }
      // Ensure the updated entity reflects the changes (adapter might not update the primary flag correctly if not part of changes)
      // This is redundant if changes.primary is handled, but good for safety
      if (
        state.entities[action.payload.id] &&
        action.payload.primary !== undefined
      ) {
        state.entities[action.payload.id]!.primary = action.payload.primary;
      }
    },
    examDeleted: (state, action: PayloadAction<string>) => {
      examsAdapter.removeOne(state, action.payload);
    },
    examStatsReceived: (state, action: PayloadAction<ExamStatsUpdate[]>) => {
      action.payload.forEach((examStats) => {
        if (!state.entities[examStats.id]) return;

        examsAdapter.updateOne(state, {
          id: examStats.id,
          changes: examStats,
        });
      });
    },
    examsFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load exams';
    },
    clearExams: (state) => {
      examsAdapter.removeAll(state); // Use adapter to clear
      state.primaryExamId = null;
      state.status = 'idle';
      state.error = null;
      state.lastFetchedAt = null;
    },
    setPrimaryExam: (state, action: PayloadAction<string>) => {
      const newPrimaryId = action.payload;
      const oldPrimaryId = state.primaryExamId;

      // Ensure the new primary ID exists before proceeding
      if (state.entities[newPrimaryId]) {
        // Unset the old primary flag if there was one and it's different
        if (
          oldPrimaryId &&
          oldPrimaryId !== newPrimaryId &&
          state.entities[oldPrimaryId]
        ) {
          state.entities[oldPrimaryId]!.primary = false;
        }
        // Set the new primary flag
        state.entities[newPrimaryId]!.primary = true;
        // Update the primaryExamId state field
        state.primaryExamId = newPrimaryId;
      }
    },
    customExamsReordered: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((examId, index) => {
        if (state.entities[examId]) {
          state.entities[examId]!.customExamSortOrder = index;
        }
      });
    },
  },
  // Listen for logout action from authSlice to clear exams data
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      examsAdapter.removeAll(state);
      state.primaryExamId = null;
      state.status = 'idle';
      state.error = null;
      state.lastFetchedAt = null;
    });
  },
});

export const {
  examsLoading,
  examsReceived,
  examCreated,
  examUpdated,
  examStatsReceived,
  examsFailed,
  setPrimaryExam,
  customExamsReordered,
} = examsSlice.actions;

const selectExamsState = (state: RootState) => state.exams;
const examAdapterSelectors = examsAdapter.getSelectors(selectExamsState);
const selectAllExams = examAdapterSelectors.selectAll;
const selectExamEntities = examAdapterSelectors.selectEntities;

const selectPrimaryExam = (state: RootState) => {
  const { primaryExamId } = selectExamsState(state);
  const entities = selectExamEntities(state);
  return primaryExamId ? entities[primaryExamId] : undefined;
};

// Export adapter selectors for easy data access
export const examsSelectors = {
  ...examAdapterSelectors,
  selectExams: selectAllExams,
  selectPrimaryExam,
  selectPrimaryExamAccessLevel: createSelector(
    [selectPrimaryExam],
    (primaryExam): SubscriptionAccess => {
      return getExamAccessLevel(primaryExam);
    }
  ),
  selectStepExams: createSelector([selectAllExams], (allExams) => {
    return allExams
      .filter(isStepExam)
      .sort((a, b) => a.name.localeCompare(b.name));
  }),
  selectShelfExams: createSelector([selectAllExams], (allExams) => {
    return allExams
      .filter(isShelfExam)
      .sort((a, b) => a.name.localeCompare(b.name));
  }),
  selectComlexExams: createSelector([selectAllExams], (allExams) => {
    return allExams
      .filter(isComlexExam)
      .sort((a, b) => a.name.localeCompare(b.name));
  }),
  selectResidencyExams: createSelector([selectAllExams], (allExams) => {
    return allExams
      .filter(isResidencyExam)
      .sort((a, b) => a.name.localeCompare(b.name));
  }),
  // Nursing exams (NCLEX-RN). Empty for a medical account until the backend
  // ships NCLEX, so pickers that render this group change nothing for them.
  selectNclexExams: createSelector([selectAllExams], (allExams) => {
    return allExams
      .filter(isNclexExam)
      .sort((a, b) => a.name.localeCompare(b.name));
  }),
  selectLicensingExams: createSelector([selectAllExams], (allExams) => {
    return allExams
      .filter(isStepExam || isComlexExam)
      .sort((a, b) => a.name.localeCompare(b.name));
  }),
  selectSchoolExams: createSelector(
    [selectAllExams, (state: RootState) => state.school.school?.id],
    (allExams, currentSchoolId) => {
      return (
        allExams
          .filter(
            (exam) =>
              // Include shared custom exams for the currently selected school.
              // Some API responses populate only exam.schoolId (without nested school),
              // so fall back to exam.schoolId when exam.school is missing.
              isSharedCustomExam(exam) &&
              (exam.school?.id === currentSchoolId ||
                exam.schoolId === currentSchoolId)
          )
          // Hide exams with fewer than 3 lectures or named "New Exam" unless the current user is the owner
          .filter(
            (exam) =>
              exam.isOwner ||
              ((exam.lectureCount ?? 0) >= 3 && exam.name !== 'New Exam')
          )
          .sort((a, b) => {
            // First, sort by verified status (verified exams first)
            const aVerified = a.verified || false;
            const bVerified = b.verified || false;

            if (aVerified && !bVerified) return -1;
            if (!aVerified && bVerified) return 1;

            // If both have same verification status and not verified, sort by ownership
            if (!aVerified && !bVerified) {
              const aIsOwner = a.isOwner || false;
              const bIsOwner = b.isOwner || false;

              if (aIsOwner && !bIsOwner) return -1;
              if (!aIsOwner && bIsOwner) return 1;
            }

            // Within the same verification/ownership group, first sort by lectureCount descending, then by vignettes.total descending
            const aLectures = a.lectureCount ?? 0;
            const bLectures = b.lectureCount ?? 0;
            if (aLectures !== bLectures) return bLectures - aLectures;

            const aContent = a.vignettes?.total ?? 0;
            const bContent = b.vignettes?.total ?? 0;
            if (aContent === bContent) return 0;
            return bContent - aContent;
          })
      );
    }
  ),
  selectCustomExams: createSelector([selectAllExams], (allExams) => {
    return allExams.filter(isPersonalExam).sort((a, b) => {
      const aSort = a.customExamSortOrder;
      const bSort = b.customExamSortOrder;
      if (aSort !== null && aSort !== undefined) {
        if (bSort === null || bSort === undefined) return -1;
        if (aSort !== bSort) return aSort - bSort;
      } else if (bSort !== null && bSort !== undefined) {
        return 1;
      }

      if (!a.examDate && !b.examDate) return 0;
      if (!a.examDate) return 1;
      if (!b.examDate) return -1;
      return new Date(a.examDate).getTime() - new Date(b.examDate).getTime();
    });
  }),
  selectPreclinicalExam: createSelector([selectAllExams], (allExams) =>
    allExams.find((exam) => isPreclinicalExam(exam))
  ),
  selectActiveCustomExam: createSelector([selectAllExams], (allExams) =>
    allExams.find(
      (exam) => exam.type === 'CUSTOM' && exam.isActiveCustomExam === true
    )
  ),
};
// Example usage: const allExams = useSelector(examsSelectors.selectAll);
// Example usage: const oneExam = useSelector(state => examsSelectors.selectById(state, examId));

export default examsSlice.reducer;
